import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { ListSkeleton, ErrorState } from "@/components/shared/StateViews";
import * as LucideIcons from "lucide-react";
import { Lock, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { publicAvatarDescription } from "@/lib/avatarUi";
import { contentAgents } from "@/lib/agents";

const PLAN_LABELS = { pro: "Pro", premium: "Premium" };

// Starts copy for the selected public (FR26 / Story 5.2). Lists ready agents; locked
// agents show a padlock + an inline explanatory drawer (no navigation). Tapping
// a ready agent creates a thread with the avatar fixed and opens /agent/{slug}
// — the 2-tap avatar-first path.
export default function WriteCopySheet({ avatar, peerAvatars, open, onOpenChange }) {
  const nav = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [starting, setStarting] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/agents");
      setAgents(contentAgents(data));
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    else { setExpanded(null); setStarting(null); }
  }, [open]);

  const openAgent = async (agent) => {
    if (starting) return;
    setStarting(agent.slug);
    try {
      await api.post("/threads", { agent_slug: agent.slug, avatar_id: avatar.id });
      nav(`/agent/${agent.slug}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(friendlyError(err));
      setStarting(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="write-copy-sheet"
        className="bg-brand-surface border-brand-line rounded-t-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden scrollbar-soft"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-2xl text-brand-ink">
            Criar copy para {publicAvatarDescription(avatar, peerAvatars)} ✦
          </SheetTitle>
          <SheetDescription className="text-brand-ink2">
            Escolha o agente. A conversa começa com este público já selecionado.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-2.5">
          {loading && <ListSkeleton count={4} />}
          {!loading && error && <ErrorState message={error} onRetry={load} testid="write-copy-error" />}
          {!loading && !error && agents.map((agent) => {
            const Icon = LucideIcons[agent.icone] || Sparkles;
            const locked = agent.status === "locked_plan";
            const isExpanded = expanded === agent.slug;
            return (
              <div key={agent.slug}>
                <button
                  type="button"
                  data-testid={`write-copy-agent-${agent.slug}`}
                  disabled={starting === agent.slug}
                  onClick={() => (locked ? setExpanded(isExpanded ? null : agent.slug) : openAgent(agent))}
                  className={`w-full flex items-center gap-3.5 text-left rounded-2xl border p-3.5 transition-colors min-h-[44px] ${
                    locked
                      ? "border-brand-line bg-brand-elevated/40 opacity-80"
                      : "border-brand-line bg-brand-elevated hover:border-brand-rose"
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${locked ? "bg-brand-ink/10 text-brand-ink2" : "bg-brand-elevated text-brand-rose"}`}>
                    {locked ? <Lock className="w-4 h-4" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-lg text-brand-ink truncate">{agent.nome}</div>
                    <div className="text-[12.5px] text-brand-ink2 font-ui truncate">{agent.descricao}</div>
                  </div>
                  {locked ? (
                    <span className="shrink-0 text-[10.5px] tracking-[0.16em] uppercase text-brand-gold font-ui">
                      {PLAN_LABELS[agent.required_plan] || "Pro"}
                    </span>
                  ) : (
                    <ArrowRight className="w-4 h-4 text-brand-rose shrink-0" />
                  )}
                </button>
                {locked && isExpanded && (
                  <div
                    data-testid={`write-copy-locked-${agent.slug}`}
                    className="mt-1 mx-1 px-4 py-3 rounded-2xl bg-brand-elevated/60 border border-brand-line text-[12.5px] text-brand-ink2"
                  >
                    Este agente faz parte do plano{" "}
                    <span className="text-brand-gold">{PLAN_LABELS[agent.required_plan] || "Pro"}</span>.
                    Faça upgrade para desbloquear e escrever copy com ele.
                  </div>
                )}
              </div>
            );
          })}
          {!loading && !error && agents.length === 0 && (
            <div className="text-[13.5px] text-brand-ink2 text-center py-6">
              Nenhum agente disponível ainda.
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
